import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { Image, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, NavigationHeader, Spacer } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import LogoBankAccount from 'design-system/LogoBankAccount';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { selectFeatureFlag } from 'reducers/selectors';
import Amplitude from 'utils/amplitude';
import { getBalance } from 'utils/api/getBalance';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount, formatSortCode } from 'utils/formatting';
import links from 'utils/links';
import Modal from 'utils/packages/Modal';
import { OnboardingFlow } from 'utils/types/navigation';
import { getFooterText } from './AutosaveSelectBank';
const directDebitLogo = require('../../img/directDebitLogo.png');
const AutosaveSelectFundingAccount = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const onDirectDebitSetup = route.params?.onDirectDebitSetup;
    const scrollY = useSharedValue(0);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const linkedAccount = route.params.account;
    const directDebitFlag = useAppSelector((store) => selectFeatureFlag(store, 'direct_debit_provider'));
    const directDebitInfo = useMemo(() => {
        if (directDebitFlag.extra?.providerName &&
            directDebitFlag.extra?.providerPrivacyUrl &&
            directDebitFlag.extra?.transactionMemo) {
            return {
                providerName: directDebitFlag.extra.providerName,
                providerPrivacyUrl: directDebitFlag.extra.providerPrivacyUrl,
            };
        }
        return {
            providerName: 'GoCardless',
            providerPrivacyUrl: 'https://gocardless.com/privacy/',
        };
    }, [
        directDebitFlag.extra?.providerName,
        directDebitFlag.extra?.providerPrivacyUrl,
        directDebitFlag.extra?.transactionMemo,
    ]);
    const [primaryBalance] = getBalance(linkedAccount);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if (!mountedOnAnotherScreen) {
            Amplitude.logEvent('Confirm Direct Debit Account', { type: 'autosave' });
        }
    }, [mountedOnAnotherScreen]);
    const handleContinue = useCallback(() => {
        if (!('ukSortCode' in linkedAccount) && linkedAccount.type === 'SAVINGS') {
            Modal.showConfirmation({
                title: 'Direct Debit isn’t available',
                subtitle: 'Your funding account is a savings account, which doesn‘t support Direct Debit. To change your funding account, please get in touch with our support team.',
                confirmButtonTitle: 'Okay',
                cancelButtonTitle: 'Contact support',
                onPressCancel: () => {
                    navigation.navigate('Support');
                },
            });
            return;
        }
        navigation.navigate('AutosaveConfirmBankDetails', { bankAccount: linkedAccount, onDirectDebitSetup });
    }, [linkedAccount, onDirectDebitSetup]);
    const openPrivacyNotice = useCallback(() => {
        links.openLink(directDebitInfo.providerPrivacyUrl);
    }, [directDebitInfo.providerPrivacyUrl]);
    const formattedSortCode = useMemo(() => {
        if ('sortCode' in linkedAccount) {
            return formatSortCode(linkedAccount.sortCode);
        }
        if ('ukSortCode' in linkedAccount) {
            return formatSortCode(linkedAccount.ukSortCode);
        }
        return '';
    }, [linkedAccount]);
    const accountNumber = useMemo(() => {
        if ('accountNumber' in linkedAccount) {
            return linkedAccount.accountNumber;
        }
        return linkedAccount.ukAccountNumber;
    }, [linkedAccount]);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    const isDisabled = useMemo(() => {
        if ('id' in linkedAccount) {
            return !linkedAccount.id;
        }
        return !linkedAccount.accountId;
    }, [linkedAccount]);
    const name = useMemo(() => {
        if (linkedAccount) {
            if ('accountNumber' in linkedAccount) {
                return linkedAccount.name;
            }
            return linkedAccount.bankName || 'Linked bank';
        }
        return 'Linked bank';
    }, [linkedAccount]);
    return (<AppView type="secondary" withFrame={false}>
      <NavigationHeader scrollY={scrollY} outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Pots}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightContent}>
            <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title="Set up your Direct Debit" subTitle="We’ll use this account to collect payments via Direct Debit"/>

        <Spacer h={8}/>

        <CardView noMarginTop>
          <View style={styles.fundingAccountCard}>
            <View style={styles.row}>
              <View style={styles.iconContainer}>
                <LogoBankAccount size="R44" onCard data={linkedAccount}/>
              </View>
              <Spacer w={16}/>
              <View style={styles.flex}>
                <Text Text-12 Primary>
                  FUNDING ACCOUNT
                </Text>
                <Text Text-16 numberOfLines={1}>
                  {name}
                </Text>
                <Text TextThin-14 Secondary>
                  {formattedSortCode} {accountNumber}
                </Text>
              </View>
              {primaryBalance ? (<Text Numbers-16>{formatAmount(primaryBalance.amount, primaryBalance.currency, 2)}</Text>) : null}
            </View>
          </View>
        </CardView>

        <CardView withPadding noMarginTop>
          <View>
            <Text TextThin-14 Secondary>
              Payments by “{directDebitInfo.providerName}”. Read the{' '}
              <Text TextThin-14 Primary onPress={openPrivacyNotice} suppressHighlighting>
                {directDebitInfo.providerName} privacy notice.
              </Text>
            </Text>
          </View>
          <Spacer h={16}/>
          <View style={styles.footer}>
            <View style={styles.flex}>
              <Text TextThin-14 Secondary>
                Your payments are protected by the Direct Debit Guarantee.
              </Text>
            </View>
            <Spacer.W16 />
            <Image source={directDebitLogo} style={styles.img}/>
          </View>
          <View style={styles.footerText}>
            <Text TextThin-14 Secondary>
              {getFooterText(directDebitInfo.providerName)}
            </Text>
          </View>
        </CardView>
        <Spacer.H16 />
      </Animated.ScrollView>
      <Button floating brand title="Continue" onPress={handleContinue} disabled={isDisabled}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    fundingAccountCard: {
        padding: rem(16),
    },
    iconContainer: {
        width: rem(44),
        height: rem(44),
        alignItems: 'center',
        borderRadius: rem(22),
        justifyContent: 'center',
        borderWidth: rem(1),
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.contrast,
    },
    footer: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    footerText: {
        paddingTop: rem(16),
    },
    flex: {
        flex: 1,
    },
    img: {
        width: rem(80),
        height: rem(40),
        tintColor: colors.text.primary,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    rightContent: {
        marginRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
}));
export default AutosaveSelectFundingAccount;
